import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import {
    Send, Settings, ServerCog, Loader2, Check,
    FileDown, RotateCcw, ShieldAlert, ShieldCheck, ChevronDown, ChevronUp, Upload,
} from "lucide-react";
import { InvioPipeline } from "../components/InvioPipeline";
import { SecretInput } from "../components/SecretInput";
import { OfficialReceiptMenu } from "../components/OfficialReceiptMenu";

const ACTIVE = ["queued", "controllo", "autentica", "invio", "ricevuta"];
const STATUS_BADGE = {
    queued: "bg-orange-50 text-black border-orange-200",
    controllo: "bg-orange-50 text-orange-800 border-orange-200",
    autentica: "bg-orange-50 text-orange-800 border-orange-200",
    invio: "bg-orange-50 text-orange-800 border-orange-200",
    ricevuta: "bg-orange-50 text-orange-800 border-orange-200",
    completed: "bg-orange-50 text-orange-800 border-orange-200",
    awaiting_confirmation: "bg-orange-50 text-orange-800 border-orange-300",
    failed: "bg-orange-50 text-orange-800 border-orange-200",
};

export default function Telematico() {
    const { t } = useI18n();
    const [searchParams] = useSearchParams();
    const tab = searchParams.get("tab") || "settings";

    return (
        <div className="min-h-screen p-6 lg:p-10 max-w-5xl mx-auto">
            <header className="mb-6 rounded-lg bg-primary px-5 py-4 text-white">
                <h1 className="font-display text-3xl font-bold uppercase tracking-tight">{tab === "settings" ? "ENTRATEL" : t.telematico.tabInvii}</h1>
                <p className="text-sm font-bold uppercase text-white/90 mt-1">{tab === "settings" ? "Impostazioni account per ambiente e credenziali Entratel." : t.telematico.subtitle}</p>
            </header>

            {tab === "invii" ? <InviiList /> : <TelematicoSettings />}
        </div>
    );
}

function TabBtn({ active, onClick, icon, label, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
            {icon}{label}
        </button>
    );
}

function InviiList() {
    const { t } = useI18n();
    const [invii, setInvii] = useState(null);
    const [expanded, setExpanded] = useState({});
    const timerRef = useRef(null);

    const load = async () => {
        try {
            const r = await api.get("/telematico/invii");
            setInvii(r.data);
            return r.data;
        } catch { return []; }
    };

    useEffect(() => {
        let stopped = false;
        const tick = async () => {
            const data = await load();
            if (stopped) return;
            const hasActive = (data || []).some((i) => ACTIVE.includes(i.status));
            timerRef.current = setTimeout(tick, hasActive ? 2000 : 10000);
        };
        tick();
        return () => { stopped = true; clearTimeout(timerRef.current); };
    }, []);

    const retry = async (id) => {
        try {
            await api.post(`/telematico/invii/${id}/retry`);
            toast.success(t.telematico.retryStarted);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const downloadRicevuta = async (id, fmt = "RF") => {
        const tid = toast.loading("Download ricevuta...");
        try {
            const r = await api.post(`/telematico/invii/${id}/ricevuta-token?fmt=${fmt}`);
            const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            const a = document.createElement("a");
            a.href = `${backendUrl}${r.data.url}`;
            a.download = r.data.filename;
            a.rel = "noopener";
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => a.remove(), 1500);
            toast.success(t.telematico.ricevutaDownloaded, { id: tid });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore download", { id: tid });
        }
    };

    const [fetchingRicevuta, setFetchingRicevuta] = useState({});
    const fetchRicevuta = async (id) => {
        setFetchingRicevuta((p) => ({ ...p, [id]: true }));
        try {
            await api.post(`/telematico/invii/${id}/ricevuta/fetch`);
            toast.success(t.telematico.ricevutaFetched);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore recupero ricevuta");
        } finally {
            setFetchingRicevuta((p) => ({ ...p, [id]: false }));
        }
    };


    if (invii === null) return <div className="text-sm text-muted-foreground p-6">{t.common.loading}</div>;

    if (invii.length === 0) {
        return (
            <div data-testid="invii-empty" className="rounded-lg border border-border bg-card p-16 text-center">
                <Send size={32} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="font-display text-lg font-medium mb-1">{t.telematico.empty}</h3>
                <p className="text-sm text-muted-foreground">{t.telematico.emptyHint}</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {invii.map((inv) => {
                const isOpen = expanded[inv.id] ?? ACTIVE.includes(inv.status);
                return (
                    <div key={inv.id} data-testid={`invio-card-${inv.id}`} className="rounded-lg border border-border bg-card overflow-hidden">
                        <div
                            className="px-5 py-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-accent/30 transition-colors"
                            onClick={() => setExpanded((p) => ({ ...p, [inv.id]: !isOpen }))}
                        >
                            <div className="min-w-0">
                                <div className="font-medium text-sm truncate">{inv.pratica_title || inv.module_code}</div>
                                <div className="text-xs text-muted-foreground">
                                    {inv.module_code} · {new Date(inv.created_at).toLocaleString("it-IT")}
                                    <span className="ms-2 font-semibold text-orange-700">[Produzione]</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {ACTIVE.includes(inv.status) && <Loader2 size={14} className="animate-spin text-primary" />}
                                <span data-testid={`invio-status-${inv.id}`} className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${STATUS_BADGE[inv.status] || ""}`}>
                                    {t.telematico.status[inv.status] || inv.status}
                                </span>
                                {isOpen ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
                            </div>
                        </div>

                        {isOpen && (
                            <div className="px-5 pb-4 border-t border-border pt-4">
                                <InvioPipeline phases={inv.pipeline_fasi || inv.steps || []} />
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs rounded-md border border-border bg-background-alt px-3 py-2">
                                    <DetailRow label="Adempimento" value={inv.tipo_adempimento || "—"} />
                                    <DetailRow label="Ambiente" value={inv.environment || inv.mode_label || inv.mode || "—"} />
                                    <DetailRow label="Codice fiscale" value={inv.codice_fiscale || "—"} mono />
                                    <DetailRow label="Partita IVA" value={inv.partita_iva || "—"} mono />
                                    <DetailRow label="File" value={inv.ccf_file_name || "—"} />
                                    <DetailRow label="SHA-256 file" value={inv.ccf_sha256 || "—"} mono />
                                </div>
                                {inv.protocol && (
                                    <div className="mt-3 text-sm">
                                        <span className="text-muted-foreground">{t.telematico.protocol}:</span>{" "}
                                        <span data-testid={`invio-protocol-${inv.id}`} className="font-mono font-semibold">{inv.protocol}</span>
                                    </div>
                                )}
                                {inv.esito && (
                                    <div className="mt-2 flex items-center gap-2 text-sm">
                                        <span className="text-muted-foreground">{t.telematico.esitoLabel}:</span>
                                        <span data-testid={`invio-esito-${inv.id}`} className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${inv.esito === "ACCOLTA" ? "bg-orange-50 text-orange-800 border-orange-200" : inv.esito === "SCARTATA" ? "bg-orange-50 text-orange-800 border-orange-200" : "bg-orange-50 text-orange-800 border-orange-200"}`}>
                                            {t.telematico.esito[inv.esito] || inv.esito}
                                        </span>
                                    </div>
                                )}
                                {(inv.ricevuta_path || inv.ricevuta_rel_path) && (
                                    <div data-testid={`ricevuta-official-${inv.id}`} className="mt-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div>
                                                <div className="text-xs font-semibold text-orange-900">{t.telematico.ricevutaOfficial}</div>
                                                <div className="text-[11px] text-orange-800/80">{t.telematico.ricevutaAvailableHint}</div>
                                            </div>
                                            <OfficialReceiptMenu invioId={inv.id} onDownload={downloadRicevuta} />
                                        </div>
                                    </div>
                                )}
                                {inv.error && <InvioErrorBox inv={inv} />}
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {inv.status === "completed" && inv.mode !== "simulazione" && !inv.esito && (
                                        <button data-testid={`fetch-ricevuta-${inv.id}`} onClick={() => fetchRicevuta(inv.id)} disabled={fetchingRicevuta[inv.id]} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-accent text-xs font-medium transition-colors disabled:opacity-50">
                                            {fetchingRicevuta[inv.id] ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} {t.telematico.fetchRicevuta}
                                        </button>
                                    )}
                                    {inv.status === "failed" && (
                                        <button data-testid={`retry-invio-${inv.id}`} onClick={() => retry(inv.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-accent text-xs font-medium transition-colors">
                                            <RotateCcw size={12} /> {t.telematico.retry}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function InvioErrorBox({ inv }) {
    const simple = inv.error?.startsWith("ERRORE INVIO:")
        ? inv.error
        : `ERRORE INVIO: problema controllo/autentica/invio. ${inv.error || "La procedura si è fermata: il file non è stato inviato."}`;
    const stepError = (inv.steps || []).find((s) => s.status === "error");
    const technical = inv.error_detail || stepError?.technical_log || stepError?.log || inv.error || "Nessun dettaglio tecnico disponibile per questo invio.";

    return (
        <div data-testid={`invio-error-${inv.id}`} className="mt-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900">
            <div className="font-semibold">{simple}</div>
            <details className="mt-3 text-xs text-orange-800">
                <summary
                    data-testid={`toggle-errore-tecnico-${inv.id}`}
                    className="inline-flex cursor-pointer select-none items-center rounded-md border border-orange-300 bg-white px-3 py-1.5 font-semibold text-orange-900 hover:bg-orange-100"
                >
                    Mostra dettagli tecnici
                </summary>
                <pre data-testid={`errore-tecnico-${inv.id}`} className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded border border-orange-200 bg-white/80 p-2 font-mono text-[11px]">{technical}</pre>
            </details>
        </div>
    );
}


function DetailRow({ label, value, mono, testid }) {
    return (
        <div className="flex items-start gap-3 text-sm">
            <div className="w-32 flex-shrink-0 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground pt-0.5">{label}</div>
            <div data-testid={testid} className={`min-w-0 break-all ${mono ? "font-mono text-xs" : "font-medium"}`}>{value}</div>
        </div>
    );
}

function TelematicoSettings() {
    const { t } = useI18n();
    const [form, setForm] = useState(null);
    const [server, setServer] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploadingEnv, setUploadingEnv] = useState(false);
    const passwordRef = useRef(null);
    const pinRef = useRef(null);
    const envPasswordRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const [s, st] = await Promise.all([api.get("/telematico/settings"), api.get("/telematico/status")]);
                setForm({ ...s.data, password: "", pin: "", env_password: "" });
                setServer(st.data);
            } catch {}
        })();
    }, []);

    if (!form) return <div className="text-sm text-muted-foreground p-6">{t.common.loading}</div>;

    const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const revealSecret = async (field) => {
        try {
            const r = await api.post("/telematico/settings/reveal", { field });
            upd(field, r.data.value || "");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossibile mostrare la credenziale salvata");
            throw e;
        }
    };

    const uploadEnvironment = async (files) => {
        if (!files || files.length === 0) return;
        setUploadingEnv(true);
        const fd = new FormData();
        Array.from(files).forEach((f) => fd.append("files", f));
        try {
            const r = await api.post("/telematico/environment", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((p) => ({ ...p, environment: r.data.environment, env_security_path: r.data.environment?.path || p.env_security_path }));
            toast.success(t.telematico.environment.uploaded);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore upload ambiente sicurezza");
        } finally { setUploadingEnv(false); }
    };

    const save = async () => {
        setBusy(true);
        try {
            // Read the DOM value as well as React state: mobile password managers and
            // browser autofill do not always emit the input event React relies on.
            const password = passwordRef.current?.value || form.password || "";
            const pin = pinRef.current?.value || form.pin || "";
            const envPassword = envPasswordRef.current?.value || form.env_password || "";
            const payload = {
                tipo_utente: form.tipo_utente,
                utente: form.utente,
                progressivo_sede: form.progressivo_sede,
                codice_fiscale_intermediario: form.codice_fiscale_intermediario,
                invio_conto_proprio: form.invio_conto_proprio,
                incaricante_utente: form.incaricante_utente,
                incaricante_sede: form.incaricante_sede,
                env_security_path: form.env_security_path,
                mode: "produzione",
            };
            if (password) payload.password = password;
            if (pin) payload.pin = pin;
            if (envPassword) payload.env_password = envPassword;
            const r = await api.put("/telematico/settings", payload);
            if (envPassword && !r.data.has_env_password) {
                throw new Error("La password dell'ambiente di sicurezza non risulta salvata. Riprova.");
            }
            setForm({ ...r.data, password: "", pin: "", env_password: "" });
            toast.success(t.telematico.saved);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Errore salvataggio");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4">
            {/* Server status */}
            <div data-testid="telematico-server-status" className={`rounded-lg border px-4 py-3 flex items-start gap-3 ${server?.installed ? "border-orange-300 bg-orange-50" : server?.block_reason ? "border-orange-300 bg-orange-50" : "border-orange-300 bg-orange-50"}`}>
                <ServerCog size={18} className={`mt-0.5 ${server?.installed ? "text-orange-700" : server?.block_reason ? "text-orange-700" : "text-orange-700"}`} />
                <div className="text-sm">
                    <div className="font-semibold">{server?.installed ? t.telematico.installed : server?.block_reason ? t.telematico.blockedTitle : t.telematico.notInstalled}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{server?.installed ? (server?.configured_home || "") : (server?.block_reason || t.telematico.simulationNote)}</div>
                </div>
            </div>

            {server?.installed && (
                <div className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground">
                    Produzione telematica disponibile. I dettagli diagnostici interni sono visibili solo lato amministrazione/server.
                </div>
            )}

            <div className="rounded-lg border border-border bg-card p-5 space-y-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground mb-3">{t.telematico.settingsForm.credentialsSection}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label={t.telematico.settingsForm.tipoUtente}>
                            <select data-testid="tel-tipo-utente" value={form.tipo_utente} onChange={(e) => upd("tipo_utente", e.target.value)} className={inputCls}>
                                <option value="fisconline">Fisconline</option>
                                <option value="entratel">Entratel</option>
                            </select>
                        </Field>
                        <Field label={t.telematico.settingsForm.utente}>
                            <input data-testid="tel-utente" value={form.utente} onChange={(e) => upd("utente", e.target.value)} className={inputCls} placeholder="Codice utente" />
                        </Field>
                        <Field label={`${t.telematico.settingsForm.password}${form.has_password ? " ✓" : ""}`} hint={t.telematico.settingsForm.passwordHint}>
                            <SecretInput inputRef={passwordRef} testid="tel-password" value={form.password} onChange={(value) => upd("password", value)} placeholder={form.has_password ? "••••••••  (già salvata)" : ""} hasSavedValue={form.has_password} onRevealSaved={() => revealSecret("password")} />
                        </Field>
                        <Field label={`${t.telematico.settingsForm.pin}${form.has_pin ? " ✓" : ""}`} hint={t.telematico.settingsForm.pinHint}>
                            <SecretInput inputRef={pinRef} testid="tel-pin" value={form.pin} onChange={(value) => upd("pin", value)} placeholder={form.has_pin ? "••••••••  (già salvato)" : ""} hasSavedValue={form.has_pin} onRevealSaved={() => revealSecret("pin")} />
                        </Field>
                        <Field label={t.telematico.settingsForm.cfIntermediario} hint={t.telematico.settingsForm.cfIntermediarioHint}>
                            <input data-testid="tel-cf-intermediario" value={form.codice_fiscale_intermediario} onChange={(e) => upd("codice_fiscale_intermediario", e.target.value.toUpperCase())} className={inputCls} maxLength={16} />
                        </Field>
                        <Field label={t.telematico.settingsForm.invioPer} full>
                            <div className="space-y-2 rounded-md border border-border bg-background-alt p-3 text-sm">
                                <label className="flex items-center gap-2">
                                    <input type="radio" name="invio-conto" checked={form.invio_conto_proprio !== false} onChange={() => upd("invio_conto_proprio", true)} />
                                    {t.telematico.settingsForm.contoProprio}
                                </label>
                                <label className="flex items-center gap-2">
                                    <input type="radio" name="invio-conto" checked={form.invio_conto_proprio === false} onChange={() => upd("invio_conto_proprio", false)} />
                                    {t.telematico.settingsForm.contoAltro}
                                </label>
                            </div>
                        </Field>
                        {form.invio_conto_proprio === false && <>
                            <Field label={t.telematico.settingsForm.incaricanteUtente} hint={t.telematico.settingsForm.incaricanteUtenteHint}>
                                <input data-testid="tel-incaricante-utente" value={form.incaricante_utente || ""} onChange={(e) => upd("incaricante_utente", e.target.value.toUpperCase())} className={inputCls} />
                            </Field>
                            <Field label={t.telematico.settingsForm.incaricanteSede} hint={t.telematico.settingsForm.incaricanteSedeHint}>
                                <input data-testid="tel-incaricante-sede" value={form.incaricante_sede || ""} onChange={(e) => upd("incaricante_sede", e.target.value)} className={inputCls} inputMode="numeric" maxLength={3} placeholder="000" />
                            </Field>
                        </>}
                        <Field label={t.telematico.settingsForm.envPath} full>
                            <input data-testid="tel-env-path" value={form.env_security_path} onChange={(e) => upd("env_security_path", e.target.value)} className={inputCls} placeholder="/percorso/ambiente-sicurezza" />
                        </Field>
                        <Field label={`${t.telematico.settingsForm.envPassword}${form.has_env_password ? " ✓" : ""}`} hint={t.telematico.settingsForm.envPasswordHint} full>
                            <SecretInput inputRef={envPasswordRef} testid="tel-env-password" value={form.env_password} onChange={(value) => upd("env_password", value)} placeholder={form.has_env_password ? "••••••••  (già salvata)" : ""} hasSavedValue={form.has_env_password} onRevealSaved={() => revealSecret("env_password")} />
                        </Field>
                    </div>
                </div>

                <div>
                    <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground mb-3">{t.telematico.environment.title}</div>
                    <div data-testid="tel-env-upload-box" className={`rounded-md border px-4 py-3 ${form.environment?.ready ? "border-orange-300 bg-orange-50" : "border-orange-300 bg-orange-50"}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="text-sm">
                                <div className={`font-semibold ${form.environment?.ready ? "text-orange-800" : "text-orange-900"}`}>
                                    {form.environment?.ready
                                        ? t.telematico.environment.ready
                                        : form.environment?.files?.length
                                            ? t.telematico.environment.partial
                                            : t.telematico.environment.missing}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">{form.environment?.path || t.telematico.environment.pathHint}</div>
                                {form.environment?.files?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {form.environment.files.map((f) => (
                                            <span key={f.name} className="rounded border border-border bg-white/70 px-2 py-0.5 text-[11px] font-mono">{f.name}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-accent">
                                {uploadingEnv ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                {uploadingEnv ? t.common.loading : (form.environment?.files?.length ? t.telematico.environment.replaceBtn : t.telematico.environment.uploadBtn)}
                                <input data-testid="tel-env-upload" type="file" multiple accept=".P12,.p12,.KS,.ks,.txt" className="hidden" disabled={uploadingEnv} onChange={(e) => { uploadEnvironment(e.target.files); e.target.value = ""; }} />
                            </label>
                        </div>
                        <div className="mt-2 text-[11px] text-muted-foreground">{t.telematico.environment.allowed}</div>
                    </div>
                </div>

                <div>
                    <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground mb-3">{t.telematico.settingsForm.modeSection}</div>
                    <div className="grid grid-cols-1 gap-3">
                        <ModeCard
                            testid="tel-mode-production"
                            active={true}
                            disabled={!server?.installed}
                            onClick={() => server?.installed && upd("mode", "produzione")}
                            icon={<ShieldAlert size={16} />}
                            title={t.telematico.settingsForm.modeProduction}
                            desc={server?.installed ? t.telematico.settingsForm.modeProductionDesc : t.telematico.settingsForm.modeRealDisabled}
                        />
                    </div>
                </div>

                <div className="flex justify-end pt-2">
                    <button data-testid="tel-save-settings" onClick={save} disabled={busy} className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors disabled:opacity-50">
                        {busy ? t.common.loading : t.common.save}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ModeCard({ active, disabled, onClick, icon, title, desc, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            disabled={disabled}
            className={`text-start rounded-md border px-4 py-3 transition-colors ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-accent"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
            <div className={`flex items-center gap-2 text-sm font-semibold ${active ? "text-primary" : ""}`}>{icon}{title}</div>
            <div className="text-xs text-muted-foreground mt-1">{desc}</div>
        </button>
    );
}

function Field({ label, children, full, hint }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">{label}</div>
            {children}
            {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
        </div>
    );
}

const inputCls = "w-full px-3 py-2 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm";
